import { useState, useEffect } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { BracketInfo } from "../../types/custom_types";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import Navbar from "./navbar";
import Footer from "./footer";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

// ---- Toggle this to false when the tournament starts to close bracket entry ----
const ENTRY_OPEN = false;
// ---------------------------------------------------------------------------------

const hallOfFame = [
  { year: "2022", winner: "Lori", score: 110 },
  { year: "2023", winner: "J. Dough-nuts", score: 135 },
  { year: "2024", winner: "Jan", score: 136 },
  { year: "2025", winner: "Erin", score: 153 },
  { year: "2026", winner: "McMoney", score: 158 },
];

const BracketList = () => {
  const [brackets, setBrackets] = useState<BracketInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    const fetchBrackets = async () => {
      const response = await axios.get<BracketInfo[]>(
        `${process.env.REACT_APP_API_URL}/userapi/brackets/`,
      );
      setBrackets(response.data);
      setLoading(false);
    };

    fetchBrackets();
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: theme.gradients.contentBg,
      }}
    >
      <Navbar />

      {/* Hero — 3-stop gradient from theme */}
      <Box
        sx={{
          background: theme.gradients.hero,
          color: "white",
          py: { xs: 6, md: 8 },
          textAlign: "center",
          px: 2,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{ fontFamily: theme.fonts.display, mb: 1 }}
        >
          Welcome to the Tournament
        </Typography>
        <Typography
          variant="h5"
          sx={{ color: "secondary.light", fontWeight: 500 }}
        >
          March Madness 2026
        </Typography>
        {ENTRY_OPEN && (
          <Button
            component={RouterLink}
            to="/create-bracket"
            variant="contained"
            color="secondary"
            size="large"
            sx={{ mt: 3, fontWeight: 700, fontSize: "1.1rem", px: 4 }}
          >
            Create Your Bracket
          </Button>
        )}
      </Box>

      <Container maxWidth="md" sx={{ py: 4, flex: 1 }}>
        {/* Rules */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography
            variant="h6"
            color="primary"
            fontWeight={700}
            gutterBottom
          >
            Tournament Rules
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <List dense disablePadding>
            {[
              "Entry closes when the first Round 1 game starts.",
              "The First Four games do not receive points.",
              "The highest total score wins.",
              "Point values are doubled every round.",
              "You may enter multiple brackets, but each incurs a $25 entry fee.",
              "Fees should be paid directly to the winner at the end.",
            ].map((rule, i) => (
              <ListItem key={i} sx={{ py: 0.25, px: 0 }}>
                <ListItemText primary={`• ${rule}`} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Rankings */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography variant="h6" color="primary" fontWeight={700}>
              Rankings
            </Typography>
            <Button
              component={RouterLink}
              to="/compare"
              variant="outlined"
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            >
              Compare All
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && brackets.length === 0 && (
            <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
              No brackets have been created yet.
            </Typography>
          )}
          {brackets.map((bracket, index) => (
            <Card
              key={bracket.id}
              variant="outlined"
              sx={{
                mb: 1.5,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: 4 },
                // Accent left border in secondary color
                borderLeft: "4px solid",
                borderLeftColor:
                  index === 0 ? "secondary.main" : "primary.light",
              }}
            >
              <CardActionArea
                component={RouterLink}
                to={`/bracket/${bracket.id}`}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: 1.5,
                    "&:last-child": { pb: 1.5 },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: index === 0 ? "secondary.main" : "primary.main",
                      color:
                        index === 0
                          ? "secondary.contrastText"
                          : "primary.contrastText",
                      width: 36,
                      height: 36,
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    #{index + 1}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={600}>{bracket.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {bracket.username}
                    </Typography>
                  </Box>
                  {/* #1 gets a gradient chip, rest get a plain outlined chip */}
                  {index === 0 ? (
                    <Chip
                      label={`${bracket.score} pts`}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
                        color: theme.palette.secondary.contrastText,
                        border: "none",
                      }}
                    />
                  ) : (
                    <Chip
                      label={`${bracket.score} pts`}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Paper>

        {/* Hall of Fame */}
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography
            variant="h6"
            color="primary"
            fontWeight={700}
            gutterBottom
          >
            ★ Hall of Fame ★
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "center",
            }}
          >
            {hallOfFame.map(({ year, winner, score }) => (
              <Card
                key={year}
                variant="outlined"
                sx={{
                  textAlign: "center",
                  p: 2,
                  minWidth: 130,
                  flex: "1 1 130px",
                  maxWidth: 180,
                  borderTop: "3px solid",
                  borderTopColor: "secondary.main",
                }}
              >
                <Typography variant="h6" color="primary" fontWeight={700}>
                  {year}
                </Typography>
                <EmojiEventsIcon
                  sx={{ fontSize: 40, color: "secondary.main", my: 1 }}
                />
                <Typography
                  sx={{
                    fontFamily: theme.fonts.display,
                    fontSize: "1rem",
                    color: "primary.main",
                  }}
                >
                  {winner}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {score} pts
                </Typography>
              </Card>
            ))}
          </Box>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default BracketList;
